package floris.graphics.backoffice.pages.admin

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import com.varabyte.kobweb.compose.css.TextDecorationLine
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Arrangement.SpaceBetween
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.borderBottom
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.minHeight
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.textDecorationLine
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.layout.SimpleGrid
import com.varabyte.kobweb.silk.components.layout.numColumns
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.breakpoint.ResponsiveValues
import com.varabyte.kobweb.silk.theme.breakpoint.rememberBreakpoint
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import floris.graphics.backoffice.components.H4SpanTextVariant
import floris.graphics.backoffice.components.layouts.PageLayout
import floris.graphics.backoffice.pages.admin.categories.CategoryCard
import floris.graphics.backoffice.pages.admin.contributors.ContributorCardContent
import floris.graphics.backoffice.pages.admin.projects.ProjectCard
import floris.graphics.backoffice.pages.admin.techstacks.TechstackCard
import floris.graphics.core.navigation.Navigation
import com.materialdesignsystem.components.widgets.DsCard
import com.materialdesignsystem.components.widgets.DsCreateCard
import com.materialdesignsystem.components.widgets.TextButton
import com.materialdesignsystem.toColorScheme
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import kotlinx.coroutines.flow.collectLatest
import org.jetbrains.compose.web.css.Color
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.Text

@Page("/admin/index")
@Composable
fun AdminPage() {
    val ctx = rememberPageContext()

    val viewModel = remember { AdminHomeViewModel() }
    val state by viewModel.state.collectAsState()

    LaunchedEffect(Unit) {
        viewModel.eventFlow.collectLatest {
            when (it) {
                is AdminHomeUiEvent.Navigate -> ctx.router.navigateTo(it.route)
            }
        }
    }

    AdminPageContent(state = state, onEvent = viewModel::onEvent)
}

@Composable
fun AdminPageContent(
    state: AdminHomeState,
    onEvent: (AdminHomeEvent) -> Unit
) {
    val breakpoint = rememberBreakpoint()
    val colorScheme = ColorMode.current.toColorScheme

    PageLayout("Admin") {
        Column(
            modifier = Modifier
                .fillMaxWidth(if (breakpoint > Breakpoint.MD) 80.percent else 90.percent)
                .padding(top = if (breakpoint >= Breakpoint.MD) 58.px else 36.px),
            verticalArrangement = Arrangement.spacedBy(120.px)
        ) {
            val greeting = if (state.isLoading) {
                "Hello"
            } else {
                "Hello, ${state.currentUser?.fullName ?: "Admin"}!"
            }

            H1 { Text(greeting) }

            Column(
                modifier = Modifier.fillMaxWidth(),
                verticalArrangement = Arrangement.spacedBy(24.px)
            ) {
                HeadlineSection(
                    modifier = Modifier.fillMaxWidth(),
                    title = "Categorieën",
                    onSeeAllClick = { onEvent(AdminHomeEvent.Navigate(Navigation.Screen.Admin.Categories.Index.route)) }
                )

                HomeGrid {
                    DsCreateCard(
                        modifier = Modifier.minHeight(90.px),
                        text = "Category toevoegen",
                        contentColor = colorScheme.outline,
                        iconSize = 32.px,
                        isColumn = false,
                        href = Navigation.Screen.Admin.Categories.Manage().route
                    )

                    state.categories.forEach { category ->
                        CategoryCard(
                            modifier = Modifier.fillMaxWidth(),
                            category = category
                        )
                    }
                }
            }

            Column(
                modifier = Modifier.fillMaxWidth(),
                verticalArrangement = Arrangement.spacedBy(24.px)
            ) {
                HeadlineSection(
                    modifier = Modifier.fillMaxWidth(),
                    title = "Contributeuren",
                    onSeeAllClick = { onEvent(AdminHomeEvent.Navigate(Navigation.Screen.Admin.Contributors.Index.route)) }
                )

                HomeGrid {
                    DsCreateCard(
                        modifier = Modifier.minHeight(220.px),
                        text = "Contributeur toevoegen",
                        contentColor = colorScheme.outline,
                        iconSize = 64.px,
                        href = Navigation.Screen.Admin.Contributors.Manage().route
                    )

                    state.contributors.forEach { contributor ->
                        A(
                            href = Navigation.Screen.Admin.Contributors.Manage(contributor.id).route,
                            attrs = Modifier
                                .textDecorationLine(TextDecorationLine.None)
                                .toAttrs()
                        ) {
                            DsCard(
                                content = {
                                    ContributorCardContent(contributor = contributor)
                                }
                            )
                        }
                    }
                }
            }

            Column(
                modifier = Modifier.fillMaxWidth(),
                verticalArrangement = Arrangement.spacedBy(24.px)
            ) {
                HeadlineSection(
                    modifier = Modifier.fillMaxWidth(),
                    title = "Technology Stacks",
                    onSeeAllClick = { onEvent(AdminHomeEvent.Navigate(Navigation.Screen.Admin.Techstacks.Index.route)) }
                )

                HomeGrid(
                    numColumns = numColumns(1, 2, 3, 3, 4)
                ) {
                    DsCreateCard(
                        modifier = Modifier.minHeight(if (breakpoint < Breakpoint.SM) 220.px else 150.px),
                        text = "Technology stack toevoegen",
                        contentColor = colorScheme.outline,
                        iconSize = 64.px,
                        href = Navigation.Screen.Admin.Techstacks.Manage().route
                    )

                    state.techStacks.forEach { item ->
                        TechstackCard(item)
                    }
                }
            }

            Column(
                modifier = Modifier.fillMaxWidth(),
                verticalArrangement = Arrangement.spacedBy(24.px)
            ) {
                HeadlineSection(
                    modifier = Modifier.fillMaxWidth(),
                    title = "Projecten",
                    onSeeAllClick = { onEvent(AdminHomeEvent.Navigate(Navigation.Screen.Admin.Projects.Index.route)) }
                )

                HomeGrid(
                    numColumns = numColumns(1, xl = 2, xxl = 3)
                ) {
                    DsCreateCard(
                        modifier = Modifier
                            .minHeight(
                                when (breakpoint) {
                                    Breakpoint.ZERO -> 200.px
                                    Breakpoint.SM -> 320.px
                                    else -> 360.px
                                }
                            ),
                        text = "Project toevoegen",
                        contentColor = colorScheme.outline,
                        iconSize = if (breakpoint < Breakpoint.SM) 64.px else 100.px,
                        href = Navigation.Screen.Admin.Projects.Manage().route
                    )

                    state.projects.forEach { project ->
                        ProjectCard(project = project)
                    }
                }
            }
        }
    }
}

@Composable
private fun HomeGrid(
    numColumns: ResponsiveValues<Int>? = null,
    content: @Composable () -> Unit
) {
    val breakpoint = rememberBreakpoint()

    SimpleGrid(
        modifier = Modifier
            .fillMaxWidth()
            .gap(
                rowGap = if (breakpoint > Breakpoint.MD) 36.px else 24.px,
                columnGap = if (breakpoint > Breakpoint.MD) 36.px else 24.px
            ),
        numColumns = numColumns ?:  numColumns(base = 1, sm = 2, xl = 3, xxl = 4),
        content = content
    )
}

@Composable
private fun HeadlineSection(
    modifier: Modifier = Modifier,
    title: String,
    onSeeAllClick: () -> Unit
) {
    Row(
        modifier = modifier
            .borderBottom(1.px, LineStyle.Solid, Color.gray),
        horizontalArrangement = SpaceBetween,
        verticalAlignment = Alignment.CenterVertically
    ) {
        SpanText(
            text = title,
            variant = H4SpanTextVariant,
        )

        TextButton(
            onClick = { onSeeAllClick() },
            content = {
                Text("Zie alles")
            }
        )
    }
}
